// GameTour.jsx — reusable spotlight guided tour.
// Dims the background, highlights the active control, and shows a popover with
// Next / Back / Skip and "Step X of N". Steps whose target element is missing are
// shown centered (no spotlight) so the tour never breaks.
import React, { useEffect, useState, useCallback } from 'react';
import './GameTour.css';

const POPOVER_W = 280;

function computeLayout(rect, viewport) {
  if (!rect) return { popover: {}, arrow: 'bottom', spot: null };
  const left = Math.min(
    Math.max(12, rect.left + rect.width / 2 - POPOVER_W / 2),
    viewport.width - POPOVER_W - 12
  );
  const placeAbove = rect.top > viewport.height * 0.55;
  if (placeAbove) {
    return {
      popover: { top: Math.max(8, rect.top - 14), left, transform: 'translateY(-100%)' },
      arrow: 'bottom',
      spot: rect,
    };
  }
  return {
    popover: { top: rect.top + rect.height + 14, left },
    arrow: 'top',
    spot: rect,
  };
}

const GameTour = ({ steps = [], onFinish }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);

  const step = steps[index];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => {
    measure();
    const onResize = () => { setIsMobile(window.innerWidth < 640); measure(); };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const finish = () => { if (onFinish) onFinish(); };
  const next = () => { if (index + 1 >= steps.length) finish(); else setIndex(index + 1); };
  const back = () => setIndex(Math.max(0, index - 1));

  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const layout = isMobile ? { popover: {}, arrow: 'top', spot: null } : computeLayout(rect, viewport);

  return (
    <div className="gt-overlay">
      {/* Dimmed spotlight (desktop) */}
      {!isMobile && rect && (
        <>
          <div className="gt-shade" style={{ top: 0, left: 0, right: 0, height: rect.top }} />
          <div className="gt-shade" style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }} />
          <div className="gt-shade" style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
          <div className="gt-shade" style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }} />
          <div className="gt-highlight" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
        </>
      )}
      {/* Mobile: simple full dim */}
      {isMobile && <div className="gt-shade" style={{ inset: 0 }} />}

      {/* Popover */}
      <div
        className={`gt-popover ${isMobile ? 'gt-popover-mobile' : ''}`}
        style={isMobile ? undefined : layout.popover}
      >
        {!isMobile && <div className={`gt-arrow gt-arrow-${layout.arrow}`} />}
        <div className="gt-step-count">Step {index + 1} of {steps.length}</div>
        <h3 className="gt-title">{step?.title}</h3>
        <p className="gt-desc">{step?.description}</p>
        <div className="gt-controls">
          <button className="gt-skip" onClick={finish}>Skip tutorial</button>
          <div className="gt-nav">
            {index > 0 && <button className="gt-back" onClick={back}>Back</button>}
            <button className="gt-next" onClick={next}>
              {index + 1 === steps.length ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
        <div className="gt-dots">
          {steps.map((_, i) => (
            <span key={i} className={`gt-dot ${i === index ? 'active' : ''}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GameTour;
